/**
 * @file UserContextsEditor.tsx
 * @description Редактор контекстов пользователя (phase08.5 shadcn).
 */

import { Plus, ShieldUser, Store, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";

import { Alert } from "./ui/alert";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Label } from "./ui/label";
import { Separator } from "./ui/separator";
import { useNotify } from "./ui/NotifyProvider";
import {
  addAdminUserContext,
  removeAdminUserContext,
  type AdminUser,
  type AdminUserContext,
} from "../lib/api-admin-users";
import { listAdminRoles, type AdminRole } from "../lib/api-admin-roles";
import { ApiError } from "../lib/api-client";
import { cn } from "@/lib/utils";

interface ShopScopeProp {
  scope: "shop";
  shopId: string;
  shopDisplayName: string;
}
interface PlatformScopeProp {
  scope: "platform";
  shops: Array<{ id: string; slug: string; displayName: string }>;
}

interface Props {
  user: AdminUser;
  scope: ShopScopeProp | PlatformScopeProp;
  onChanged: (user: AdminUser) => void;
}

function CtxIcon({ kind }: { kind: "PLATFORM" | "SHOP" }) {
  const Icon = kind === "PLATFORM" ? ShieldUser : Store;
  return (
    <div
      className={cn(
        "size-9 rounded-full flex items-center justify-center shrink-0",
        kind === "PLATFORM"
          ? "bg-primary/15 text-primary"
          : "bg-secondary text-secondary-foreground",
      )}
    >
      <Icon className="size-5" />
    </div>
  );
}

const nativeSelectCls =
  "flex h-10 w-full rounded-md bg-input px-3 py-2 text-sm text-on-surface outline-none focus-visible:bg-surface-lowest focus-visible:[box-shadow:inset_0_-2px_0_var(--primary)] disabled:opacity-50";

export function UserContextsEditor({ user, scope, onChanged }: Props) {
  const [adding, setAdding] = useState(false);
  const [contextType, setContextType] = useState<"PLATFORM" | "SHOP">(
    scope.scope === "shop" ? "SHOP" : "PLATFORM",
  );
  const [shopId, setShopId] = useState<string>(
    scope.scope === "shop" ? scope.shopId : "",
  );
  const [roleId, setRoleId] = useState<string>("");
  const [roles, setRoles] = useState<AdminRole[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingRemove, setPendingRemove] = useState<AdminUserContext | null>(null);
  const [removing, setRemoving] = useState(false);
  const notify = useNotify();

  const hasShopOwner = user.contexts.some(
    (c) => c.contextType === "SHOP" && c.role.code === "SHOP_OWNER",
  );
  const hasAnyPlatform = user.contexts.some((c) => c.contextType === "PLATFORM");

  function roleDisabledReason(r: AdminRole): string | null {
    if (r.category === "PLATFORM" && hasShopOwner) {
      return "Невозможно: пользователь уже «Владелец магазина». Сначала снимите эту роль.";
    }
    if (r.code === "SHOP_OWNER" && hasAnyPlatform) {
      return "Невозможно: у пользователя есть платформенная роль. Сначала снимите все PLATFORM-контексты.";
    }
    return null;
  }
  const platformOptionDisabled = hasShopOwner;

  useEffect(() => {
    if (!adding) return;
    listAdminRoles(contextType)
      .then(setRoles)
      .catch(() => setRoles([]));
  }, [adding, contextType]);

  function onRemove(ctx: AdminUserContext) {
    setPendingRemove(ctx);
  }

  async function confirmRemove() {
    if (!pendingRemove) return;
    setRemoving(true);
    try {
      const updated = await removeAdminUserContext(user.id, pendingRemove.id);
      onChanged(updated);
      setPendingRemove(null);
    } catch (err) {
      notify.error(
        err instanceof ApiError ? err.message : "Не удалось удалить контекст",
      );
    } finally {
      setRemoving(false);
    }
  }

  async function onAdd(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!roleId) {
      setError("Выберите роль");
      return;
    }
    if (contextType === "SHOP" && !shopId) {
      setError("Выберите магазин");
      return;
    }
    setSubmitting(true);
    try {
      const updated = await addAdminUserContext(user.id, {
        contextType,
        shopId: contextType === "SHOP" ? shopId : undefined,
        roleId,
      });
      onChanged(updated);
      setRoleId("");
      if (scope.scope === "platform" && contextType === "SHOP") setShopId("");
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Не удалось добавить контекст",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <div className="label-editorial mb-2">Назначенные контексты</div>
        {user.contexts.length === 0 ? (
          <p className="text-sm text-on-surface-variant py-2">
            Нет назначенных контекстов
          </p>
        ) : (
          <div className="flex flex-col gap-1">
            {user.contexts.map((ctx) => {
              const scopeLabel =
                ctx.contextType === "PLATFORM"
                  ? "Платформа"
                  : ctx.shop?.displayName ?? ctx.shop?.slug ?? "?";
              return (
                <div
                  key={ctx.id}
                  className="flex items-center gap-3 py-2"
                >
                  <CtxIcon kind={ctx.contextType} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-on-surface">
                      {ctx.role.displayName}
                    </div>
                    <div className="text-xs text-on-surface-variant">{scopeLabel}</div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => onRemove(ctx)}
                    aria-label="Удалить контекст"
                    className="text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Separator />

      {!adding ? (
        <Button variant="outline" onClick={() => setAdding(true)} className="self-start">
          <Plus className="size-4" />
          Добавить контекст
        </Button>
      ) : (
        <form
          onSubmit={(e) => void onAdd(e as React.FormEvent)}
          className="flex flex-col gap-3 pt-1"
        >
          {scope.scope === "platform" ? (
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 flex flex-col gap-1.5">
                <Label htmlFor="ctx-type">Тип контекста</Label>
                <select
                  id="ctx-type"
                  value={contextType}
                  onChange={(e) => {
                    const next = e.target.value as "PLATFORM" | "SHOP";
                    setContextType(next);
                    setRoleId("");
                    if (next === "PLATFORM") setShopId("");
                  }}
                  disabled={submitting}
                  className={nativeSelectCls}
                >
                  <option value="PLATFORM" disabled={platformOptionDisabled}>
                    Платформа
                    {platformOptionDisabled
                      ? " — недоступно (юзер уже Владелец магазина)"
                      : ""}
                  </option>
                  <option value="SHOP">Магазин</option>
                </select>
              </div>
              {contextType === "SHOP" ? (
                <div className="flex-1 flex flex-col gap-1.5">
                  <Label htmlFor="ctx-shop">Магазин</Label>
                  <select
                    id="ctx-shop"
                    value={shopId}
                    onChange={(e) => setShopId(e.target.value)}
                    disabled={submitting}
                    className={nativeSelectCls}
                  >
                    <option value="">— выбрать —</option>
                    {scope.shops.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.displayName}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
            </div>
          ) : (
            <p className="text-sm text-on-surface-variant">
              Магазин: <strong>{scope.shopDisplayName}</strong>
            </p>
          )}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ctx-role">Роль</Label>
            <select
              id="ctx-role"
              value={roleId}
              onChange={(e) => setRoleId(e.target.value)}
              disabled={submitting}
              className={nativeSelectCls}
            >
              <option value="">— выбрать —</option>
              {roles.map((r) => {
                const disabledReason = roleDisabledReason(r);
                return (
                  <option
                    key={r.id}
                    value={r.id}
                    disabled={!!disabledReason}
                    title={disabledReason ?? undefined}
                  >
                    {r.displayName}
                    {disabledReason ? " — недоступно" : ""}
                  </option>
                );
              })}
            </select>
          </div>

          {error ? <Alert severity="error">{error}</Alert> : null}

          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              type="button"
              onClick={() => {
                setAdding(false);
                setError(null);
                setRoleId("");
              }}
              disabled={submitting}
            >
              Отмена
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? "Сохраняем…" : "Добавить"}
            </Button>
          </div>
        </form>
      )}

      <Dialog
        open={!!pendingRemove}
        onOpenChange={(o) => !o && !removing && setPendingRemove(null)}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Удалить контекст?</DialogTitle>
            <DialogDescription>
              {pendingRemove
                ? `Контекст «${pendingRemove.role.displayName}» будет удалён. Пользователь потеряет доступ в этом scope.`
                : ""}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setPendingRemove(null)}
              disabled={removing}
            >
              Отмена
            </Button>
            <Button
              variant="destructive"
              onClick={() => void confirmRemove()}
              disabled={removing}
            >
              {removing ? "Удаляем…" : "Удалить"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
